import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { isEmail } from 'validator';
import { track } from '../../../utils/analytics';

import {
	clearReferenceRequest,
	getQuestionProfilePreview,
	newReferenceRequest,
} from '../../../actions/referenceRequestActions';
import routes from '../../../constants/routes';

import InfoIcon from '../../../../assets/info-icon.svg';
import * as Styled from './styled';

import { SpinnerWithWrapper } from '../../../components/common/spinner';
import QuestionsPreview from './questions-preview';
import RemindersPreview from './reminders-preview';
import SuccessMessage from './success-message';

import CloseIcon from '../../../../assets/close-ref-icon.svg';
import NewRequestIcon from '../../../../assets/new-request-icon.svg';

import { HelpOutlineOutlined, Info, Visibility } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogContent,
	Divider,
	FormControlLabel,
	Grid,
	IconButton,
	InputAdornment,
	Link,
	ListSubheader,
	MenuItem,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import VanillaToasts from 'vanillatoasts';
import axios from 'axios';
import MuiTelInput from '../../../components/common/mui-tel-input';
import { API_URL, POST_CHECK_FOR_EXISTING_REQUEST_EMAIL } from '../../../constants/api';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

const isEnabled = (value) => value === true || Number(value) === 1;
const hasPositiveValue = (value) => Number(value) > 0;
const hasDbsCreditCost = (creditCost = {}) =>
	hasPositiveValue(creditCost.dbs_basic_credits) ||
	hasPositiveValue(creditCost.dbs_enhanced_credits);
const profileHasDbsCheck = (profile) =>
	!!profile &&
	(profile.dbsCheckOnly ||
		hasPositiveValue(profile.dbsCheckTypeId) ||
		hasPositiveValue(profile.dbsDetails?.dbs_check_type_id) ||
		hasDbsCreditCost(profile.dbsDetails?.credit_cost));

const checkForExistingRequest = async (email) => {
	try {
		const response = await axios.post(
			`${API_URL}${POST_CHECK_FOR_EXISTING_REQUEST_EMAIL}`,
			new URLSearchParams({
				inputemail: email,
			}),
			{ withCredentials: true }
		);

		if (response.data.status === 'success') {
			return response.data.data.count;
		} else {
			throw new Error('Failed to check for existing request');
		}
	} catch (error) {
		console.error(error);
	}
};

export const ReferenceRequest = ({
	clearReferenceRequest,
	reminder1,
	reminder2,
	reminder3,
	reminder4,
	isMobile,
	onClose = () => {},
	open,
	creditBalance,
	rtwIdCreditBalance,
	digitalIdCreditBalance,
	meteredBilling,
	loadingContent = false,
	isSuperAdmin,
	newReferenceRequest,
	questionPreview,
	profileList = [],
	requestCreated,
	requestId,
	sendingRequest,
	getQuestionProfilePreview,
}) => {
	const defaultReminders = {
		1: reminder1,
		2: reminder2,
		3: reminder3,
		4: reminder4,
	};
	const { formatMessage } = useIntl();
	const {
		register,
		handleSubmit,
		formState: { errors },
		control,
		setError,
		clearErrors,
		getValues,
		setValue,
		reset,
		watch,
		trigger,
		getFieldState,
	} = useForm({
		defaultValues: {
			fullname: '',
			email: '',
			phone: '',
			customMessage: '',
			questionProfileId: '',
			references: 2,
			rightToWorkMandatory: false,
			digitalIdType: 1, // Default to RTW type
		},
	});
	const questionProfileList = profileList.map(
		({
			name,
			qpid,
			is_rtw_check,
			is_dbs_check,
			can_yrequired,
			can_message,
			dbs_details,
			dbs_check_type_id,
			digital_id_type,
		}) => ({
			name,
			value: qpid,
			rtwCheck: isEnabled(is_rtw_check),
			dbsCheckOnly: isEnabled(is_dbs_check),
			rtwCheckMandatory: isEnabled(can_yrequired),
			customMessage: can_message,
			dbsDetails: dbs_details,
			dbsCheckTypeId: dbs_check_type_id || dbs_details?.dbs_check_type_id || 0,
			digitalIdType: digital_id_type || 1,
		})
	);
	const [selectedProfile, setSelectedProfile] = useState(null);
	const [rightToWorkCheckProfile, setRightToWorkCheckProfile] = useState(null);
	const [dbsCheckOnlyProfile, setDbsCheckOnlyProfile] = useState(null);
	const numOfReferencesList = Array.from({ length: 10 }, (_, i) => i + 1).map((value) => ({
		name: formatMessage({ id: 'ReferenceRequest.labels.references' }, { references: value }),
		value,
	}));
	const [reminders, setReminders] = useState(defaultReminders);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalContent, setModalContent] = useState('');
	const [showCreditsCost, setShowCreditsCost] = useState(false);
	const [intlTelInput, setIntlTelInput] = useState(null);
	const [credits, setCredits] = useState([]);
	const [emailExistsWarning, setEmailExistsWarning] = useState(false);
	const [addMore, setAddMore] = useState(false);
	const nameInputRef = useRef(null);

	const watchRightToWorkMandatory = useWatch({ control, name: 'rightToWorkMandatory' });
	const watchDigitalIdType = useWatch({ control, name: 'digitalIdType' });
	const watchEmail = watch('email');
	const selectedProfileHasDbsCheck = profileHasDbsCheck(selectedProfile);

	useEffect(() => {
		if (!open) {
			resetForm();
			setModalOpen(false);
		} else {
			// Reset addMore when dialog opens
			setAddMore(false);
		}
	}, [open]);

	// Focus the name input when the modal opens
	useEffect(() => {
		if (open && nameInputRef.current) {
			// Small delay to ensure the dialog transition has completed
			const timer = setTimeout(() => {
				nameInputRef.current.focus();
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [open]);

	// Handle "Add more" when request is created
	useEffect(() => {
		if (requestCreated && addMore) {
			// Show success notification
			VanillaToasts.create({
				type: 'success',
				timeout: 3000,
				title: formatMessage({ id: 'Notifications.success' }),
				text: formatMessage({ id: 'ReferenceRequest.labels.newRequestCreated' }),
			});

			// Clear only candidate-specific fields, keep settings
			setValue('fullname', '');
			setValue('email', '');
			setValue('phone', '');
			// Reset the phone input component
			if (intlTelInput) {
				intlTelInput.setNumber('');
			}
			setEmailExistsWarning(false);

			// Clear the request created state so form stays visible
			clearReferenceRequest();

			// Focus on name input for next candidate
			setTimeout(() => {
				nameInputRef.current?.focus();
			}, 100);
		}
	}, [requestCreated, addMore, formatMessage, setValue, clearReferenceRequest, intlTelInput]);

	useEffect(() => {
		if (Object.keys(errors).length) {
			const errorObj = Object.entries(errors).reduce((acc, [key, value]) => {
				return {
					...acc,
					[key]: value.message,
				};
			}, {});

			track('Request form errors', {
				errors: errorObj,
			});
		}
	}, [errors]);

	useEffect(() => {
		const subscription = watch(({ questionProfileId }) => {
			if (questionProfileId) {
				setShowCreditsCost(true);
			} else {
				setShowCreditsCost(false);
			}
			trigger('rightToWorkMandatory');
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [watch]);

	useEffect(() => {
		const calculateCredits = () => {
			const calculatedCredits = [];
			const creditCost = selectedProfile?.dbsDetails?.credit_cost || {};

			if (!selectedProfile) return calculatedCredits;

			// Add reference credit for standard checks
			if (!selectedProfile.rtwCheck && !selectedProfile.dbsCheckOnly) {
				calculatedCredits.push('reference');
			}

			// Add DBS credits if specified in credit_cost
			if (creditCost.dbs_basic_credits) calculatedCredits.push('dbsBasic');
			if (creditCost.dbs_enhanced_credits) calculatedCredits.push('dbsEnhanced');

			// Add RTW ID credit for DBS with RTW check
			if (creditCost.id_credits) calculatedCredits.push('rtwId');
			// Add Digital ID credit for DBS with non-RTW ID check
			if (creditCost.id_credits_basic) calculatedCredits.push('digitalId');

			// Add ID credit based on profile type or RTW requirement (standalone ID checks)
			if (
				!selectedProfileHasDbsCheck &&
				(selectedProfile.rtwCheck || watchRightToWorkMandatory)
			) {
				// Digital ID Type: 1 = RTW, 2/3 = non-RTW
				if (watchDigitalIdType === 1) {
					if (!calculatedCredits.includes('rtwId')) calculatedCredits.push('rtwId');
				} else {
					if (!calculatedCredits.includes('digitalId'))
						calculatedCredits.push('digitalId');
				}
			}

			return calculatedCredits;
		};

		setCredits(calculateCredits());
	}, [
		selectedProfile,
		selectedProfileHasDbsCheck,
		watchRightToWorkMandatory,
		watchDigitalIdType,
	]);

	const onSubmit = (data) => {
		const submittedProfile =
			questionProfileList.find((profile) => profile.value === data.questionProfileId) ||
			selectedProfile;
		const submittedProfileHasDbsCheck = profileHasDbsCheck(submittedProfile);
		const formData = {
			...data,
			reminders,
			phone: intlTelInput?.getNumber(),
			phoneType: intlTelInput?.getNumberType(),
		};

		if (rightToWorkCheckProfile) {
			formData.customMessage = '';
			formData.references = 0;
			formData.rightToWorkMandatory = true;
		}

		if (dbsCheckOnlyProfile) {
			formData.customMessage = '';
			formData.references = 0;
			formData.rightToWorkMandatory = false;
		}

		if (submittedProfileHasDbsCheck) {
			formData.rightToWorkMandatory = false;
			formData.digitalIdType = 0;
		}

		track('Submit request form', {
			isMobile,
			addMoreEnabled: addMore,
		});

		// Pass addMore flag for analytics tracking
		formData.addMoreEnabled = addMore;

		newReferenceRequest(formData);
	};

	const resetForm = () => {
		clearErrors();
		reset();
		setRightToWorkCheckProfile(null);
		setDbsCheckOnlyProfile(null);
		setSelectedProfile(null);
		clearReferenceRequest();
	};

	const handleClose = (e) => {
		e.preventDefault();
		requestCreated && clearReferenceRequest();
		onClose();
	};
	const handleModal = (event, type = '') => {
		event && event.preventDefault();

		setModalContent(type);
		setModalOpen(!modalOpen);
	};
	const questionsPreviewId = 'question-profile-preview-modal';
	const remindersPreviewId = 'reminders-preview-modal;';

	const ModalOptions = {
		questions: (
			<QuestionsPreview
				id={questionsPreviewId}
				questionPreview={questionPreview}
				questionProfileId={getValues('questionProfileId')}
				getQuestionProfilePreview={getQuestionProfilePreview}
			/>
		),
		reminders: (
			<RemindersPreview
				id={remindersPreviewId}
				defaultReminderOptions={reminders}
				setReminders={setReminders}
				onClose={handleModal}
			/>
		),
	};

	const CreditUsageAlert = () => {
		const { formatMessage } = useIntl();

		if (!credits.length) return null;

		return (
			<Box>
				<Typography
					variant="body2"
					sx={{
						mb: 1,
						color: 'text.secondary',
						fontWeight: 500,
					}}
				>
					{formatMessage({ id: 'ReferenceRequest.credits.title' })}
				</Typography>
				<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
					{credits.map((creditType, index) => {
						const creditLabel = formatMessage({
							id: `ReferenceRequest.credits.${creditType}`,
						});

						return (
							<React.Fragment key={creditType}>
								<Chip
									label={`1 ${creditLabel}`}
									size="small"
									sx={{
										bgcolor: 'grey.100',
										fontWeight: 500,
										fontSize: '0.875rem',
									}}
								/>
								{index < credits.length - 1 && (
									<Typography
										variant="body2"
										sx={{
											color: 'text.secondary',
											alignSelf: 'center',
										}}
									>
										+
									</Typography>
								)}
							</React.Fragment>
						);
					})}
				</Box>
			</Box>
		);
	};

	const handleEmailBlur = async (e) => {
		const email = e.target.value;
		setEmailExistsWarning(false);
		if (email && isEmail(email)) {
			const count = await checkForExistingRequest(email);
			if (count > 0) {
				setEmailExistsWarning(true);
			}
		}
	};

	const renderEmailExistsWarning = (email) => {
		if (!emailExistsWarning) return null;
		return (
			<span>
				{formatMessage(
					{ id: 'ReferenceRequest.errors.emailExists' },
					{
						a: (linkTxt) => (
							<a
								href={`${routes.employer}#${email}`}
								onClick={(e) => {
									e.preventDefault();
									window.location.href = `${routes.employer}#${email}`;
									window.location.reload();
								}}
							>
								{linkTxt}
							</a>
						),
					}
				)}
			</span>
		);
	};

	return (
		<Styled.Container>
			<Styled.RequestContainer id="new-request-form-container">
				<Styled.FormContainer isMobile={isMobile}>
					<Styled.CloseButton
						type="button"
						onClick={handleClose}
						aria-label="Close reference request form"
						disabled={sendingRequest}
						id="request-close-button"
					>
						<CloseIcon />
					</Styled.CloseButton>
					{requestCreated ? (
						<SuccessMessage
							creditBalance={creditBalance}
							onClose={handleClose}
							startNewRefRequest={resetForm}
							requestId={requestId}
						/>
					) : (
						<>
							<Styled.FormTitle id="new-request-form">
								<NewRequestIcon />
								{formatMessage({ id: 'ReferenceRequest.title' })}
							</Styled.FormTitle>
							<Typography variant="paragraph" my={1} color="#626263">
								{formatMessage(
									{
										id: 'ReferenceRequest.creatingRequestMessage',
									},
									{
										a: (linkTxt) => (
											<Link
												sx={{
													cursor: 'pointer',
												}}
												onClick={() =>
													window.Supademo?.loadDemo(
														'cm0p0p4th0033r8mn02c982xe'
													)
												}
											>
												{linkTxt}
											</Link>
											// <a
											// 	target="_blank"
											// 	href="https://support.refnow.com/hc/en-gb/articles/13877580783389-Creating-a-reference-request"
											// 	rel="noreferrer"
											// >
											// 	{linkTxt}
											// </a>
										),
									}
								)}
							</Typography>
							{isSuperAdmin && creditBalance < 3 && !meteredBilling && (
								<Styled.CreditsBanner tabIndex={0}>
									<InfoIcon />
									<p>
										{formatMessage(
											{
												id: 'ReferenceRequest.topUpCreditsAdmin',
											},
											{
												a: (linkTxt) => (
													<a href={routes.settings.buyCredits}>
														{linkTxt}
													</a>
												),
											}
										)}
									</p>
								</Styled.CreditsBanner>
							)}
							{!isSuperAdmin && creditBalance < 3 && !meteredBilling && (
								<Styled.CreditsBanner tabIndex={0}>
									<InfoIcon />
									<p>
										{formatMessage({
											id: 'ReferenceRequest.topUpCredits',
										})}
									</p>
								</Styled.CreditsBanner>
							)}
							<Styled.Form onSubmit={handleSubmit(onSubmit)}>
								<Styled.FormBody>
									<Box mb={1}>
										<Typography
											sx={{ fontSize: '14px', color: '#626263' }}
											variant="caption"
											display="block"
										>
											{formatMessage({
												id: 'ReferenceRequest.labels.candidateDetails',
											})}
										</Typography>
										<Divider />
									</Box>
									<Grid
										container
										columnSpacing={{ xs: 1, sm: 2, md: 3 }}
										id="candidate-details"
									>
										<Grid item xs={12} sm={6}>
											<Styled.FormRow>
												<TextField
													data-intercom-target="new_reference_request_can_name"
													fullWidth
													inputRef={nameInputRef}
													id="candidate-full-name"
													name="fullname"
													type="text"
													disabled={sendingRequest}
													label={formatMessage({
														id: 'ReferenceRequest.labels.fullName',
													})}
													placeholder={formatMessage({
														id: 'ReferenceRequest.placeholders.fullName',
													})}
													variant="outlined"
													error={!!errors.fullname}
													helperText={
														!!errors.fullname && errors.fullname.message
													}
													{...register('fullname', {
														required: formatMessage({
															id: 'ReferenceRequest.errors.required',
														}),
													})}
												/>
											</Styled.FormRow>
										</Grid>
										<Grid item xs={12} sm={6}>
											<Styled.FormRow>
												<Controller
													name="email"
													control={control}
													rules={{
														required: formatMessage({
															id: 'ReferenceRequest.errors.required',
														}),
														validate: (value) =>
															isEmail(value) ||
															formatMessage({
																id: 'ReferenceRequest.errors.email',
															}),
													}}
													render={({ field, fieldState: { error } }) => (
														<TextField
															{...field}
															fullWidth
															id="candidate-email"
															data-public
															type="email"
															disabled={sendingRequest}
															label={formatMessage({
																id: 'ReferenceRequest.labels.email',
															})}
															placeholder={formatMessage({
																id: 'ReferenceRequest.placeholders.email',
															})}
															variant="outlined"
															onBlur={async (e) => {
																field.onBlur();
																await handleEmailBlur(e);
															}}
															error={!!error}
															helperText={
																(error && error.message) ||
																renderEmailExistsWarning(
																	field.value
																)
															}
														/>
													)}
												/>
											</Styled.FormRow>
										</Grid>
										<Grid item xs={12} sm={6}>
											<Styled.FormRow>
												<Controller
													control={control}
													rules={{
														validate: () =>
															intlTelInput?.isValidNumber() ||
															formatMessage({
																id: 'ReferenceRequest.errors.invalidNumber',
															}),
														required: formatMessage({
															id: 'ReferenceRequest.errors.required',
														}),
													}}
													name="phone"
													id="candidate-phonenumber"
													render={({
														fieldState: { invalid, error },
													}) => (
														<MuiTelInput
															isValidCheck={({ target }) => {
																if (intlTelInput.getNumber()) {
																	setValue('phone', target.value);
																}

																clearErrors('phone');

																if (
																	intlTelInput.getNumber() &&
																	!intlTelInput?.isValidNumber()
																) {
																	setError('phone', {
																		type: 'custom',
																		message: formatMessage({
																			id: 'ReferenceRequest.errors.invalidNumber',
																		}),
																	});
																}
															}}
															setIntlTelInput={setIntlTelInput}
															fullWidth
															disabled={sendingRequest}
															label={formatMessage({
																id: 'ReferenceRequest.labels.phone',
															})}
															variant="outlined"
															InputLabelProps={{
																shrink: true,
															}}
															error={invalid}
															helperText={invalid && error.message}
														/>
													)}
												/>
											</Styled.FormRow>
										</Grid>
										<Grid item xs={12} sm={6}>
											<Styled.FormRow id="candidate-job-title">
												<TextField
													fullWidth
													name="jobTitle"
													type="text"
													disabled={sendingRequest}
													label={formatMessage({
														id: 'ReferenceRequest.labels.jobTitle',
													})}
													placeholder={formatMessage({
														id: 'ReferenceRequest.placeholders.jobTitle',
													})}
													InputProps={{
														endAdornment: (
															<InputAdornment position="end">
																<Tooltip
																	title={formatMessage({
																		id: 'ReferenceRequest.tooltips.jobTitle',
																	})}
																	tabIndex={0}
																>
																	<HelpOutlineOutlined />
																</Tooltip>
															</InputAdornment>
														),
													}}
													maxRows={4}
													variant="outlined"
													{...register('jobTitle')}
												/>
											</Styled.FormRow>
										</Grid>
										{!rightToWorkCheckProfile && !dbsCheckOnlyProfile && (
											<Grid item xs={12} sm={12}>
												<Styled.FormRow>
													<TextField
														id="candidate-custom-message"
														InputLabelProps={{
															shrink: true,
														}}
														fullWidth
														name="customMessage"
														type="text"
														multiline
														disabled={sendingRequest}
														aria-label="candidate-custom-message"
														label={formatMessage({
															id: 'ReferenceRequest.labels.customMessage',
														})}
														placeholder={formatMessage({
															id: 'ReferenceRequest.placeholders.customMessage',
														})}
														InputProps={{
															endAdornment: (
																<InputAdornment position="end">
																	<Tooltip
																		title={formatMessage({
																			id: 'ReferenceRequest.tooltips.customMessage',
																		})}
																		tabIndex={0}
																	>
																		<HelpOutlineOutlined />
																	</Tooltip>
																</InputAdornment>
															),
														}}
														maxRows={4}
														variant="outlined"
														{...register('customMessage')}
													/>
												</Styled.FormRow>
											</Grid>
										)}
									</Grid>
									<Box my={1} mt={2}>
										<Typography
											sx={{ fontSize: '14px', color: '#626263' }}
											variant="caption"
											display="block"
										>
											{formatMessage({
												id: 'ReferenceRequest.labels.requestSettings',
											})}
										</Typography>
										<Divider />
									</Box>
									<Grid
										container
										columnSpacing={{ xs: 1, sm: 2, md: 3 }}
										id="request-settings"
									>
										<Grid item xs={12} sm={6}>
											<Styled.FormRow>
												<Controller
													id="candidate-question-profile"
													name="questionProfileId"
													control={control}
													rules={{
														required: formatMessage({
															id: 'ReferenceRequest.errors.required',
														}),
													}}
													render={({
														field,
														fieldState: { invalid, error },
													}) => (
														<TextField
															{...field}
															onChange={({ target }) => {
																const profile =
																	questionProfileList.find(
																		(profile) =>
																			profile.value ===
																			target.value
																	);

																setSelectedProfile(profile);

																if (profile) {
																	const profileIncludesDbsCheck =
																		profileHasDbsCheck(profile);

																	setRightToWorkCheckProfile(
																		profile.rtwCheck
																	);
																	setDbsCheckOnlyProfile(
																		profile.dbsCheckOnly
																	);
																	setValue(
																		'rightToWorkMandatory',
																		profileIncludesDbsCheck
																			? false
																			: profile.rtwCheckMandatory
																	);
																	setValue(
																		'digitalIdType',
																		profileIncludesDbsCheck
																			? 0
																			: profile.digitalIdType
																	);

																	if (
																		!profile.rtwCheck ||
																		!profile.dbsCheckOnly
																	) {
																		if (
																			!getFieldState(
																				'customMessage'
																			).isDirty
																		) {
																			setValue(
																				'customMessage',
																				profile.customMessage
																			);
																		}
																	}
																}

																field.onChange(target.value);
															}}
															id="candidate-question-profile"
															fullWidth
															select
															SelectProps={{
																MenuProps,
																...(getValues(
																	'questionProfileId'
																) && { IconComponent: () => null }),
															}}
															disabled={sendingRequest}
															label={formatMessage({
																id: 'ReferenceRequest.labels.questionProfile',
															})}
															aria-labelledby="candidate-question-profile"
															placeholder={formatMessage({
																id: 'ReferenceRequest.placeholders.questionProfile',
															})}
															variant="outlined"
															error={invalid}
															helperText={invalid && error.message}
															InputProps={{
																...(getValues(
																	'questionProfileId'
																) &&
																	!rightToWorkCheckProfile &&
																	!dbsCheckOnlyProfile && {
																		endAdornment: (
																			<InputAdornment position="end">
																				<IconButton
																					aria-label="toggle preview visibility"
																					title="Preview questions"
																					onClick={(
																						e
																					) => {
																						track(
																							'Questions profile preview'
																						);
																						handleModal(
																							e,
																							'questions'
																						);
																					}}
																					edge="end"
																				>
																					<Visibility />
																				</IconButton>
																			</InputAdornment>
																		),
																	}),
															}}
														>
															<ListSubheader
																color="primary"
																sx={{ lineHeight: '32px' }}
															>
																{formatMessage({
																	id: 'ReferenceRequest.labels.subHeaderRightToWork',
																})}
															</ListSubheader>
															{!questionProfileList.filter(
																({ rtwCheck }) => rtwCheck
															).length ? (
																<MenuItem
																	disabled
																	key="noItems"
																	sx={{
																		whiteSpace: 'normal',
																		wordBreak: 'break-word',
																		fontSize: '0.875rem',
																	}}
																>
																	{formatMessage({
																		id: 'ReferenceRequest.labels.noRightToWorkProfiles',
																	})}
																</MenuItem>
															) : (
																questionProfileList
																	.filter(
																		({ rtwCheck }) => rtwCheck
																	)
																	.map(({ name, value }) => (
																		<MenuItem
																			key={value}
																			value={value}
																		>
																			{name}
																		</MenuItem>
																	))
															)}
															<ListSubheader
																color="primary"
																sx={{ lineHeight: '32px' }}
															>
																{formatMessage({
																	id: 'ReferenceRequest.labels.subHeaderDbsCheckOnly',
																})}
															</ListSubheader>
															{!questionProfileList.filter(
																({ dbsCheckOnly }) => dbsCheckOnly
															).length ? (
																<MenuItem
																	disabled
																	key="noItems"
																	sx={{
																		whiteSpace: 'normal',
																		wordBreak: 'break-word',
																		fontSize: '0.875rem',
																	}}
																>
																	{formatMessage({
																		id: 'ReferenceRequest.labels.noDbsCheckOnlyProfiles',
																	})}
																</MenuItem>
															) : (
																questionProfileList
																	.filter(
																		({ dbsCheckOnly }) =>
																			dbsCheckOnly
																	)
																	.map(({ name, value }) => (
																		<MenuItem
																			key={value}
																			value={value}
																		>
																			{name}
																		</MenuItem>
																	))
															)}
															<ListSubheader
																color="primary"
																sx={{ lineHeight: '32px' }}
															>
																{formatMessage({
																	id: 'ReferenceRequest.labels.subHeaderQuestionProfiles',
																})}
															</ListSubheader>
															{questionProfileList
																.filter(
																	({ rtwCheck, dbsCheckOnly }) =>
																		!rtwCheck && !dbsCheckOnly
																)
																.map(({ name, value }) => (
																	<MenuItem
																		key={value}
																		value={value}
																	>
																		{name}
																	</MenuItem>
																))}
														</TextField>
													)}
												/>
											</Styled.FormRow>
										</Grid>
										{!rightToWorkCheckProfile && !dbsCheckOnlyProfile && (
											<>
												<Grid item xs={12} sm={6}>
													<Styled.FormRow>
														<Controller
															id="candidate-references"
															name="references"
															control={control}
															rules={{
																required: formatMessage({
																	id: 'ReferenceRequest.errors.required',
																}),
															}}
															render={({
																field,
																fieldState: { invalid, error },
															}) => (
																<TextField
																	{...field}
																	fullWidth
																	select
																	SelectProps={{
																		MenuProps,
																	}}
																	disabled={sendingRequest}
																	label={formatMessage({
																		id: 'ReferenceRequest.labels.refsRequired',
																	})}
																	variant="outlined"
																	error={invalid}
																	helperText={
																		invalid && error.message
																	}
																>
																	{numOfReferencesList.map(
																		({ name, value }) => (
																			<MenuItem
																				key={value}
																				value={value}
																			>
																				{name}
																			</MenuItem>
																		)
																	)}
																</TextField>
															)}
														/>
													</Styled.FormRow>
												</Grid>
												<Grid item xs={12} sm={6}>
													<Styled.FormRow>
														<Box display="flex" alignItems="baseline">
															<FormControlLabel
																label={formatMessage({
																	id: 'ReferenceRequest.labels.rtwCheckRequired',
																})}
																aria-label="right-to-work-check"
																id="candidate-right-to-work-checkbox"
																control={
																	<Controller
																		name="rightToWorkMandatory"
																		control={control}
																		disabled={
																			sendingRequest ||
																			selectedProfileHasDbsCheck
																		}
																		render={({ field }) => (
																			<Checkbox
																				{...field}
																				checked={
																					field.value
																				}
																				disabled={
																					sendingRequest ||
																					selectedProfileHasDbsCheck
																				}
																				onChange={({
																					target,
																				}) =>
																					field.onChange(
																						target.checked
																					)
																				}
																				inputProps={{
																					'aria-label':
																						'right-to-work-check',
																				}}
																				onKeyDown={(e) => {
																					if (
																						e.key ===
																						'Enter'
																					) {
																						e.preventDefault();
																						field.onChange(
																							!field.value
																						); // Toggle checkbox value
																					}
																				}}
																			/>
																		)}
																	/>
																}
															/>
															<Tooltip
																title={
																	<Box
																		sx={{
																			a: {
																				fontSize: '12px',
																				textDecoration:
																					'underline',
																				color: 'white',
																			},
																		}}
																	>
																		<Typography variant="caption">
																			{formatMessage({
																				id: 'ReferenceRequest.digitalIdInfoMsg',
																			})}
																		</Typography>
																		<Divider
																			sx={{
																				margin: '5px 0',
																				borderColor:
																					'white',
																			}}
																		/>
																		<Typography variant="caption">
																			{formatMessage(
																				{
																					id: 'ReferenceRequest.rtwCheckInfoMsg',
																				},
																				{
																					a: (
																						linkTxt
																					) => (
																						<a
																							target="_blank"
																							rel="noopener noreferrer"
																							href="https://www.gov.uk/government/publications/digital-identity-certification-for-right-to-work-right-to-rent-and-criminal-record-checks/digital-identity-certification-for-right-to-work-right-to-rent-and-criminal-record-checks"
																						>
																							{
																								linkTxt
																							}
																						</a>
																					),
																				}
																			)}
																		</Typography>
																	</Box>
																}
															>
																<IconButton
																	aria-label="toggle preview visibility"
																	onClick={() =>
																		track(
																			'View right-to-work info tooltip'
																		)
																	}
																>
																	<Info />
																</IconButton>
															</Tooltip>
														</Box>
													</Styled.FormRow>
												</Grid>
												{watchRightToWorkMandatory && (
													<Grid item xs={12} sm={6}>
														<Styled.FormRow>
															<Controller
																name="digitalIdType"
																control={control}
																render={({ field }) => (
																	<TextField
																		{...field}
																		fullWidth
																		select
																		size="small"
																		disabled={sendingRequest}
																		label={formatMessage({
																			id: 'ReferenceRequest.labels.digitalIdType',
																		})}
																		variant="outlined"
																	>
																		<MenuItem value={1}>
																			{formatMessage({
																				id: 'ReferenceRequest.digitalIdType.rtw',
																			})}
																		</MenuItem>
																		<MenuItem value={2}>
																			{formatMessage({
																				id: 'ReferenceRequest.digitalIdType.passport',
																			})}
																		</MenuItem>
																		<MenuItem value={3}>
																			{formatMessage({
																				id: 'ReferenceRequest.digitalIdType.passportLicense',
																			})}
																		</MenuItem>
																	</TextField>
																)}
															/>
														</Styled.FormRow>
													</Grid>
												)}
											</>
										)}
										{!dbsCheckOnlyProfile && (
											<Grid
												item
												xs={12}
												sm={6}
												display="flex"
												alignItems="center"
											>
												<Button
													id="request-reminders"
													variant="outlined"
													color="secondary"
													onClick={(e) => handleModal(e, 'reminders')}
													disabled={sendingRequest}
												>
													{formatMessage({
														id: 'ReferenceRequest.buttons.reminders',
													})}
												</Button>
											</Grid>
										)}
										{showCreditsCost && (
											<Grid
												item
												xs={12}
												display="flex"
												my={1}
												alignItems="center"
											>
												<Styled.FormRow>
													<CreditUsageAlert />
												</Styled.FormRow>
											</Grid>
										)}
									</Grid>
								</Styled.FormBody>
								<Styled.FormFooter>
									<Styled.ButtonContainer>
										{!!Object.keys(errors).length && (
											<div>
												<p>
													{formatMessage({
														id: 'ReferenceRequest.errors.requiredFields',
													})}
												</p>
											</div>
										)}
										<Box
											display="flex"
											alignItems="center"
											justifyContent="flex-end"
											gap={2}
										>
											<Tooltip
												title={formatMessage({
													id: 'ReferenceRequest.addMoreTooltip',
												})}
												arrow
											>
												<FormControlLabel
													control={
														<Switch
															checked={addMore}
															onChange={(e) =>
																setAddMore(e.target.checked)
															}
															size="small"
															color="primary"
														/>
													}
													label={formatMessage({
														id: 'ReferenceRequest.addMore',
													})}
													sx={{
														m: 0,
														'& .MuiFormControlLabel-label': {
															fontSize: '0.875rem',
															color: 'text.secondary',
														},
													}}
												/>
											</Tooltip>
											<LoadingButton
												variant="contained"
												color="primary"
												size="large"
												loading={sendingRequest}
												data-intercom-target="new_reference_request_send"
												type="submit"
												id="send-request-btn"
											>
												{formatMessage({
													id: 'ReferenceRequest.buttons.sendRequest',
												})}
											</LoadingButton>
										</Box>
									</Styled.ButtonContainer>
								</Styled.FormFooter>
							</Styled.Form>
						</>
					)}
				</Styled.FormContainer>
				<Dialog
					open={modalOpen}
					maxWidth="lg"
					fullWidth
					PaperProps={{
						sx: {
							maxHeight: 'calc(80vh - 40px)',
							overflow: 'hidden',
						},
					}}
					disableEscapeKeyDown
					keepMounted
					onClose={() => setModalOpen(false)}
					aria-modal="true"
					aria-labelledby={
						modalContent === 'questions' ? questionsPreviewId : remindersPreviewId
					}
					aria-describedby="modal-preview"
				>
					<DialogContent sx={{ minHeight: '70vh' }}>
						<div>
							<Styled.CloseButton
								type="button"
								aria-label="Close modal"
								onClick={handleModal}
							>
								<CloseIcon />
							</Styled.CloseButton>
							{loadingContent ? <SpinnerWithWrapper /> : ModalOptions[modalContent]}
						</div>
					</DialogContent>
				</Dialog>
			</Styled.RequestContainer>
		</Styled.Container>
	);
};

const mapStateToProps = ({ referenceRequest, user }) => ({
	creditBalance: user.creditBal,
	rtwIdCreditBalance: user.paidRTWCreditBal,
	digitalIdCreditBalance: user.paidBasicIdCreditBal,
	meteredBilling: user.meteredBilling,
	reminder1: user.reminder1,
	reminder2: user.reminder2,
	reminder3: user.reminder3,
	reminder4: user.reminder4,
	profileList: referenceRequest.questionProfileList,
	questionPreview: referenceRequest.questionPreview,
	sendingRequest: referenceRequest.isPostingRequest,
	requestCreated: referenceRequest.requestCreated,
	requestId: referenceRequest.requestId,
});

const mapDispatchToProps = {
	clearReferenceRequest,
	getQuestionProfilePreview,
	newReferenceRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReferenceRequest);
